/**
 * Store accessor pattern to break circular dependencies.
 *
 * The store instance must be set via setStore() in store/index.ts
 * before any module attempts to access it via getStore().
 *
 * This pattern prevents circular imports between store, router, and services.
 * Unlike routerAccess, this returns null when not initialized since analytics
 * should fail gracefully rather than throwing.
 *
 * NOTE: We use Store<any> intentionally here. Importing RootState from @/store
 * would create a circular dependency (storeAccess -> store -> storeAccess).
 * The helper functions below provide type-safe access to commonly needed values.
 */

import type { InjectionKey } from 'vue';
import type { Store } from 'vuex';

// Store<any> is required to avoid circular dependency with @/store/index.ts
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let storeInstance: Store<any> | null = null;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function setStore(store: Store<any>): void {
  storeInstance = store;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getStore(): Store<any> | null {
  return storeInstance;
}

export function getCurrentUserId(): string | undefined {
  return storeInstance?.state?.user?.currentUser?.id;
}

// Vuex store injection key - defined here to avoid circular imports
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const storeKey: InjectionKey<Store<any>> = Symbol('vuex-store');
